// Audit (admin only), the improvement work (phase 4, docs/quality-
// improvement.md). Four parts:
//   - The AI share next to the auditor (QL-21): per week, the share of
//     contacts the AI handled, and for the SAME contacts the auditor's check
//     and day-7 answer, AI-handled and person-handled apart.
//   - Gaps (QL-22): contacts a person handled or changed, grouped by what the
//     person did and why the AI did not do it. "Why" is a heuristic.
//   - Candidate fixes (QL-15): a fix is config held as data (a prompt
//     addition or replacement, or a model). A replay re-runs CXHub's pipeline
//     on past contacts with NOTHING SENT, and the auditor grades old against
//     new blind.
//   - The report to Jenn: a download. There is no "apply" button: Jenn
//     approves a fix, and it ships through a normal change.
// Patrick owns the reporting views; these are plain tables until he designs
// them.

import { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Link, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { fetchQuality, postQualityFix, withdrawQualityFix, startQualityReplay, downloadQualityReport } from './api.js'
import { convNumberPath } from './contactPath.js'
import { WHY_LABEL, TAG_LABEL, OUTCOME_LABEL, pct, codeLabel, fixRequest, qualityErrorMessage, tallyLine } from './qualityLoop.js'

function useQuality(path, params) {
  const [state, setState] = useState({ data: null, error: null })
  const key = JSON.stringify(params ?? {})
  const load = useCallback(() => {
    setState({ data: null, error: null })
    fetchQuality(path, params ?? {}).then((data) => setState({ data, error: null })).catch((error) => setState({ data: null, error }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key])
  useEffect(() => { load() }, [load])
  return { ...state, reload: load }
}

function Loading({ error, children, data }) {
  if (error) return <Alert severity={error.status === 404 ? 'info' : 'error'}>{qualityErrorMessage(error)}</Alert>
  if (!data) return <CircularProgress size={24} />
  return children
}

function ShareVsAuditor() {
  const { data, error } = useQuality('share')
  return (
    <Loading data={data} error={error}>
      {data && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {data.from} to {data.to}. One row per week, counted in contacts. The auditor's numbers are for the same contacts: the
            check's pass share and the day-7 solved share. A contact in the human-ruling sample is not counted until it is ruled on.
          </Typography>
          {!data.auditor?.available && <Alert severity="info">The auditor could not be read ({codeLabel(data.auditor?.error)}): CXHub's share only.</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Week ending</TableCell><TableCell align="right">Contacts</TableCell><TableCell align="right">AI share</TableCell>
                <TableCell align="right">AI: check pass</TableCell><TableCell align="right">AI: solved</TableCell>
                <TableCell align="right">Person: check pass</TableCell><TableCell align="right">Person: solved</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.weeks.map((w) => (
                <TableRow key={w.weekEnding}>
                  <TableCell>{w.weekEnding}</TableCell>
                  <TableCell align="right">{w.contacts}</TableCell>
                  <TableCell align="right">{pct(w.aiShare)}</TableCell>
                  <TableCell align="right">{pct(w.ai.checkPassShare)} ({w.ai.checked})</TableCell>
                  <TableCell align="right">{pct(w.ai.solvedShare)} ({w.ai.audited})</TableCell>
                  <TableCell align="right">{pct(w.person.checkPassShare)} ({w.person.checked})</TableCell>
                  <TableCell align="right">{pct(w.person.solvedShare)} ({w.person.audited})</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </Loading>
  )
}

function Gaps({ onPropose }) {
  const { data, error } = useQuality('gaps')
  return (
    <Loading data={data} error={error}>
      {data && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {data.from} to {data.to}: {data.contacts} contacts a person handled or changed. "Why" is a heuristic from stored fields,
            the first rule that holds; Jenn decides what is a real gap.
            {' '}By kind: {Object.entries(data.byTag ?? {}).map(([t, n]) => `${TAG_LABEL[t] ?? t} ${n}`).join(', ') || 'none'}.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">Contacts</TableCell><TableCell>Intent</TableCell><TableCell>Why the AI did not</TableCell><TableCell>Kind</TableCell>
                <TableCell>What the person did</TableCell><TableCell align="right">Check fails</TableCell><TableCell>Sample</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.gaps.map((g) => (
                <TableRow key={g.key}>
                  <TableCell align="right">{g.contacts}</TableCell>
                  <TableCell>{codeLabel(g.intent ?? 'unknown')}</TableCell>
                  <TableCell>{WHY_LABEL[g.why] ?? g.why}</TableCell>
                  <TableCell>{TAG_LABEL[g.tag] ?? g.tag}</TableCell>
                  <TableCell>{g.did.length ? g.did.map(codeLabel).join(', ') : '—'}</TableCell>
                  <TableCell align="right">{g.auditor ? `${g.auditor.checkFail} / ${g.auditor.checked}` : '—'}</TableCell>
                  <TableCell>
                    {g.sample.slice(0, 3).map((n) => <Link key={n} href={convNumberPath(n)} sx={{ mr: 1 }}>{n}</Link>)}
                  </TableCell>
                  <TableCell><Button size="small" onClick={() => onPropose({ gapKey: g.key, window: { from: data.from, to: data.to } })}>Propose a fix</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </Loading>
  )
}

function RunDetail({ runId }) {
  const { data, error } = useQuality(`runs/${runId}`)
  return (
    <Loading data={data} error={error}>
      {data && (
        <Stack spacing={1}>
          <Typography variant="body2">
            Run {data.run.status}: {tallyLine(data.tally.overall)}. Results: {Object.entries(data.tally.resultStatus).map(([s, n]) => `${codeLabel(s)} ${n}`).join(', ') || 'none yet'}.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Contact</TableCell><TableCell>Status</TableCell><TableCell>Candidate</TableCell><TableCell>Before (redacted)</TableCell><TableCell>After (redacted)</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {data.results.map((r) => (
                <TableRow key={r.id}>
                  <TableCell><Link href={convNumberPath(r.conv_number)}>{r.conv_number}</Link></TableCell>
                  <TableCell>{codeLabel(r.status)}{r.reason ? ` (${codeLabel(r.reason)})` : ''}</TableCell>
                  <TableCell>{r.grade ? OUTCOME_LABEL[r.grade.overall] ?? r.grade.overall : '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>{r.baseline_reply ?? '(withheld or empty)'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>{r.candidate_reply ?? '(withheld or empty)'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </Loading>
  )
}

const EMPTY_FORM = { gapKey: '', window: null, title: '', stage: 'draft', mode: 'append', text: '', model: '' }

function Fixes({ proposedGap }) {
  const fixes = useQuality('fixes')
  const runs = useQuality('runs')
  const [form, setForm] = useState(EMPTY_FORM)
  const [message, setMessage] = useState(null)
  const [openRun, setOpenRun] = useState(null)
  // A gap proposed from the gaps view keeps that view's window: the replay
  // samples the same contacts.
  useEffect(() => { if (proposedGap) setForm((f) => ({ ...f, gapKey: proposedGap.gapKey, window: proposedGap.window ?? null })) }, [proposedGap])

  const models = fixes.data?.models ?? []
  async function save() {
    const r = fixRequest(form, { models })
    if (r.error) return setMessage({ severity: 'warning', text: r.error })
    try {
      await postQualityFix(r.body)
      setForm(EMPTY_FORM)
      setMessage({ severity: 'success', text: 'Fix saved as a proposal. Nothing is applied.' })
      fixes.reload()
    } catch (e) {
      setMessage({ severity: 'error', text: qualityErrorMessage(e) })
    }
  }
  async function replay(id) {
    try {
      const run = await startQualityReplay(id)
      setMessage({ severity: 'info', text: `Replay started on up to ${run.requested} contacts. Nothing is sent. Refresh to see results.` })
      runs.reload()
    } catch (e) {
      setMessage({ severity: 'error', text: qualityErrorMessage(e) })
    }
  }
  async function withdraw(id) {
    try { await withdrawQualityFix(id); fixes.reload() } catch (e) { setMessage({ severity: 'error', text: qualityErrorMessage(e) }) }
  }
  const latest = new Map()
  for (const r of runs.data?.runs ?? []) if (!latest.has(r.fix_id)) latest.set(r.fix_id, r)

  return (
    <Loading data={fixes.data} error={fixes.error}>
      {fixes.data && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            A fix is data: a prompt addition or replacement for the draft or classify stage, or a model from the allowlist
            ({models.join(', ') || 'none'}). A replay runs CXHub's pipeline twice on up to {fixes.data.replayMaxPerRun} past contacts,
            as it is and with the fix. Nothing is sent and nothing is written but the result. The auditor grades the two replies
            without knowing which is which. {fixes.data.replayEnabled ? '' : 'Replays are off (QUALITY_REPLAY_ENABLED).'}
            {fixes.data.replayEnabled ? (fixes.data.replayDailyLimitUsd > 0 ? ` Daily replay spend cap: $${fixes.data.replayDailyLimitUsd} (UTC day).` : ' No daily replay spend cap is set (QUALITY_REPLAY_DAILY_LIMIT_USD), so no run starts.') : ''}
          </Typography>
          {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField size="small" label="Gap key (or fail:<rule id>)" value={form.gapKey} onChange={(e) => setForm({ ...form, gapKey: e.target.value, window: null })} sx={{ minWidth: 320 }} />
            <TextField size="small" label="Name" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField size="small" select label="Stage" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
              <MenuItem value="draft">Draft</MenuItem><MenuItem value="classify">Classify</MenuItem>
            </TextField>
            <TextField size="small" select label="Prompt change" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <MenuItem value="append">Add to the prompt</MenuItem><MenuItem value="replace">Replace the prompt</MenuItem>
            </TextField>
            <TextField size="small" select label="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} sx={{ minWidth: 140 }}>
              <MenuItem value="">(unchanged)</MenuItem>
              {models.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField multiline minRows={3} label="Candidate prompt text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
          <Box><Button variant="contained" onClick={save}>Save the proposal</Button></Box>

          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Fix</TableCell><TableCell>Gap</TableCell><TableCell>Status</TableCell><TableCell>Latest replay</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {fixes.data.fixes.map((f) => {
                const r = latest.get(f.id)
                return (
                  <TableRow key={f.id}>
                    <TableCell>{f.title}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.gap_key}</TableCell>
                    <TableCell>{f.status}</TableCell>
                    <TableCell>
                      {r ? <Link component="button" onClick={() => setOpenRun(r.id)}>{r.status}: {tallyLine(r)}</Link> : 'Not replayed yet.'}
                    </TableCell>
                    <TableCell>
                      <Button size="small" disabled={!fixes.data.replayEnabled || f.status !== 'proposed'} onClick={() => replay(f.id)}>Replay</Button>
                      <Button size="small" disabled={f.status !== 'proposed'} onClick={() => withdraw(f.id)}>Withdraw</Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          {openRun && <RunDetail runId={openRun} />}
        </Stack>
      )}
    </Loading>
  )
}

export default function QualityLoopPanel() {
  const [proposedGap, setProposedGap] = useState(null)
  const [downloadError, setDownloadError] = useState(null)
  async function download(format) {
    setDownloadError(null)
    try { await downloadQualityReport(format) } catch (e) { setDownloadError(e.message) }
  }
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>The AI share next to the auditor</Typography>
        <ShareVsAuditor />
      </Box>
      <Box>
        <Typography variant="subtitle1" gutterBottom>Gaps: what people did that the AI did not</Typography>
        <Gaps onPropose={setProposedGap} />
      </Box>
      <Box>
        <Typography variant="subtitle1" gutterBottom>Candidate fixes, replayed with nothing sent</Typography>
        <Fixes proposedGap={proposedGap} />
      </Box>
      <Box>
        <Typography variant="subtitle1" gutterBottom>Report to Jenn</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => download('markdown')}>Download Markdown</Button>
          <Button variant="outlined" onClick={() => download('json')}>Download JSON</Button>
        </Stack>
        {downloadError && <Alert severity="error" sx={{ mt: 1 }}>Could not download the report: {downloadError}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Each gap, its candidate fixes and their before-and-after grades. Proposals only: nothing here applies a fix.
        </Typography>
      </Box>
    </Stack>
  )
}
